import Handlebars from "utilities/vendor/handlebars/4.7.7/js/handlebars";
import hbs from "utilities/vendor/handlebars/4.7.7/js/handlebars";
import "utilities/vendor/picturefill/3.0.2/js/picturefill.min";

/**
 * Handlebars Helpers:  Picture
 * Copyright (c) 2016 Bank of America
 */

/* global Handlebars */
/* eslint-disable prefer-arrow-callback */

'use strict';

// We'll base our viewport order of operations for declaring our sources on this arrray
const viewports = [
  'custom',
  'xxlarge',
  'xlarge',
  'large',
  'medium',
  'small',
];

/*
TEMPORARY
The following code is only included because the helper cannot reference it's utility dependencies
and the platform is not transpiling helpers so we cannot use the ES2015 Map features needed to
work with the data exposed by the mediaquery utility
*/
const smallMinWidth = 1;
const mediumMinWidth = 768;
const largeMinWidth = 1025;
const xlargeMinWidth = 1440;
const xxlargeMinWidth = 1921;

const mediaQueriesList = {
  small: `only screen and (min-width:${smallMinWidth}px)`,
  medium: `only screen and (min-width:${mediumMinWidth}px)`,
  large: `only screen and (min-width:${largeMinWidth}px)`,
  xlarge: `only screen and (min-width:${xlargeMinWidth}px)`,
  xxlarge: `only screen and (min-width:${xxlargeMinWidth}px)`,
};

/* END OF TEMPORARY CODE */

const LAZY_LOAD_CLASS = 'spa-lazy-image';
const DEFAULT_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

/**
 * @method generateSourceString
 * @param {Object} source single image source from JCR
 * @param {String} viewport name of current viewport size
 * @param {Boolean} isLazyLoad - coming from JCR
 * @param {String} blurrySrc - blurry image
 * @param {Boolean} custom is this a custom viewport?
 * @description Creates the `<source>` tag for a `<picture>` source based on JCR data
 * @returns {String | Boolean} `<source>` as string or false if no custom or source.media
 */
function generateSourceString(source, viewport, isLazyLoad, blurrySrc, custom) {
  // default custom to false
  // TODO: replace with default parameter value when transpilation works
  if (typeof custom === 'undefined') {
    custom = false; // eslint-disable-line no-param-reassign
  }

  // if this is a custom viewport and no media declaration was provided, exit
  if (custom && !source.media) {
    return false;
  }

  // empty string to append our attributes to
  let attributes = '';

  // check for a supplied media attribute, or use the default media for that viewport
  const media = `media="${source.media ? source.media : mediaQueriesList[viewport]}"`;

  // iterate through defined attributes for this source
  // TODO: replace for...in & Object.hasOwnProperty() with for...of when transpiling works
  Object.keys(source).forEach((attr) => {
    // if this isn't srcset or media or blurry image, just create the attrbitue string
    if (attr !== 'srcset' && attr !== 'media' && attr !== 'blurry' && !blurrySrc) {
      attributes += ` ${attr}="${source[attr]}"`;
      // we need to skip the blurry object property
    } else if (attr !== 'blurry' && (attr === 'srcset' || blurrySrc)) {
      // if blurry image is absent for lazy loading,
      // we need to set it to the default placeholder image
      const blurryImage = (!blurrySrc && isLazyLoad) ? DEFAULT_PLACEHOLDER : blurrySrc;
      // set srcset to blurry image if exists
      const srcset = (isLazyLoad)
        ? ` srcset="${blurryImage}" data-src="${source.srcset.join(',')}" ${attr}="${source[attr]}"`
        : ` srcset="${source.srcset.join(',')}" ${attr}="${source[attr]}"`;
      attributes += srcset;
    }
  });

  return `<source ${media}${attributes}>`;
}

/**
 * @method generateSourceTags
 * @param {Object} sources object of sources from JCR
 * @description Creates an array of strings representing the `<source>` elements generated by this
 * helper
 * @returns {Array} array of source strings
 */
function generateSourceTags(sources) {
  // initialize empty array to store each source for this `<picture>`
  const sourceStrings = [];

  const imageAttrs = sources.imageAttributes;
  // set to true if lazyLoad is true or lazy load class exists
  const isLazyLoad = sources.lazyLoad || (imageAttrs && imageAttrs.class
    && (imageAttrs.class.indexOf(LAZY_LOAD_CLASS) !== -1));

  // iterate through viewports
  viewports.forEach(function itertateThroughViewports(viewport) {
    // if this viewport was provided in our JCR data
    if (sources[viewport]) {
      let blurry = '';
      if (sources[viewport] instanceof Array) {
        // get blurry image from viewport array
        const blurryImage = sources[viewport].filter(value => value.blurry);
        if (blurryImage[0] && blurryImage[0].blurry) {
          blurry = blurryImage[0].blurry;
        }
      }
      // check for custom viewport declarations first
      if (viewport === 'custom') {
        // iterate through custom srcsets and generate `<source>` tags
        sources[viewport].forEach(
          function iterateThroughCustomViewports(customViewport) {
            // skip blurry property
            if (!customViewport.blurry) {
              sourceStrings.push(
                generateSourceString(customViewport, viewport, isLazyLoad,
                  blurry,
                  true));
            }
          });
      } else if (sources[viewport] instanceof Array) {
        // Sort since type image/webp needs to be first in the source lists.
        sources[viewport].sort(function sortWebpFirst(a) {
          return (a.type && a.type === 'image/webp') ? -1 : 1;
        }).forEach(function iterateThroughViewports(sourceViewport) {
          if (!sourceViewport.blurry) {
            sourceStrings.push(
              generateSourceString(sourceViewport, viewport, isLazyLoad,
                blurry));
          }
        });
      } else if (sources[viewport] instanceof Object) {
        sourceStrings.push(
          generateSourceString(sources[viewport], viewport, isLazyLoad,
            sources[viewport].blurry));
      }
    }
  });

  return sourceStrings;
}

/**
 * @method generateAttributes
 * @param {Object} attrs key value pair for attributes
 * @param {Boolean} isLazyLoad - coming from JCR
 * @returns {String} combined attributes
 */
function generateAttributes(attrs, isLazyLoad) {
  let combinedAttrs = '';
  let attributes = {};

  // add lazy load class to attributes when no other classes are coming from JCR
  if (isLazyLoad) {
    Object.assign(attrs, { class: LAZY_LOAD_CLASS });
  }
  attributes = { ...attrs };

  Object.keys(attributes).forEach((key) => {
    const attrValue = attributes[key].toString();
    const noLazyClass = attrValue && (attrValue.indexOf(LAZY_LOAD_CLASS) === -1);
    // add lazy load class to the classes coming from JCR and combine all attributes
    combinedAttrs += (key === 'class' && isLazyLoad && noLazyClass)
      ? `${key}="${attrValue} ${LAZY_LOAD_CLASS}" `
      : combinedAttrs += `${key}="${attrValue}" `;
  });

  return combinedAttrs;
}

/**
 * Generates the image tag used in the picture element
 *
 * @method generateImageTag
 * @param {Object} sources - object of sources from JCR
 * @returns {String} default or blurry image with combine attributes
 */
function generateImageTag(sources) {
  const attributes = sources.imageAttributes || {};
  const isLazyLoad = sources.lazyLoad;

  // set attribute to not load images if `skipSlow` is set to true in JCR
  const { skipSlow } = sources;
  const skipLoadingOnSlowNetwork = skipSlow ? ' data-skip-slow="true"' : '';

  // @todo deprecate alt property for the imageAttributes
  // this is backwards compatibility to move old alt prop into the imageAttributes.
  if (!attributes.alt) {
    Object.assign(attributes, {
      alt: sources.alt || '',
    });
  }

  const attrs = generateAttributes(attributes, isLazyLoad);

  return `<img src="${DEFAULT_PLACEHOLDER}" ${attrs}${skipLoadingOnSlowNetwork}/>`;
}

/**
 * @const {Object} helpers
 * @description Exposes our Handlebars helper and the name by which we will call it
 */
const helpers = {
  /**
   * @method helpers.picture
   * @param {Object} sources object of image sources for `<picture>` element
   * @returns {Node} Handlebars.SafeString() of `<picture>` element
   */
  picture: function pictureHelper(sources) {
    if (sources) {
      const tag = '<picture>';
      const closingTag = '</picture>';
      const fallbackImg = generateImageTag(sources);

      /* eslint-disable max-len */
      /*
      // DE50454 - Uncomment 134-139 to test
      const sourceTags = [
        `<source srcset="${large.src}" media="${mediaQueryUtility.get({ type: 'viewport', name: 'large' })}">`
        `<source srcset="${medium.src}" media="media="${mediaQueryUtility.get({ type: 'viewport', name: 'medium' })}">`
        `<source srcset="${small.src}" media="media="${mediaQueryUtility.get({ type: 'viewport', name: 'small' })}">`
      ];*/
      /* eslint-enable max-len */

      // DE50454 - Comment out 142 to test
      const sourceTags = generateSourceTags(sources);

      return new Handlebars.SafeString(`${tag}${sourceTags.join('')}${fallbackImg}${closingTag}`);
    }

    throw new Error('No image sources provided to picture helper.');
  },
};

/**
 * @class pictureHelper
 * @description Class used to register our helper with Handlebars
 */
class pictureHelper {
  /**
   * @method register
   * @param {Object} Handlebars reference to Handlebars for helper registration
   * @description registers helper with Handlebars
   * @returns {undefined}
   */
  static register(Handlebars) {
    Object.keys(helpers).forEach((key) => {
      Handlebars.registerHelper(key, helpers[key]);
    });
  }
}

module.exports = pictureHelper;
